import _ from 'lodash';
import { useCallback } from 'react';
import { refetchVizData } from '/@services/answer/answer.util';
import { useBachMutationWithoutSession } from '/@services/bach/bach-base';
import { useApolloClient } from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    LoadExistingAnswerSessionDocument,
    LoadExistingAnswerSessionMutation,
    LoadExistingAnswerSessionMutationVariables,
} from '../generated/graphql-types';

export const useLoadExistingAnswerSession = (
    answerSessionId: string,
    onComplete?: (sessionId: string, answerId: string) => void,
    setVisualizationsLoading = true,
    setPhrasesLoading = true,
    shouldRefetchData = true,
    copyAnswerSession = false,
) => {
    const client = useApolloClient();

    const [
        _loadAnswerSession,
        { data, loading, error },
    ] = useBachMutationWithoutSession<
        LoadExistingAnswerSessionMutation,
        LoadExistingAnswerSessionMutationVariables
    >(answerSessionId, LoadExistingAnswerSessionDocument, {
        update: (_cache, result) => {
            if (_.isFunction(onComplete)) {
                const answerId = result.data.Answer__loadSession?.answer?.id;
                onComplete(
                    result.data.Answer__loadSession.id.sessionId,
                    answerId,
                );
            }
        },
        shouldRefetchData,
        ...(shouldRefetchData
            ? {
                  customRefetchQueriesFn: (_, result) => {
                      return refetchVizData(client, result);
                  },
              }
            : {
                  refetchQueries: result => {
                      return refetchVizData(client, result);
                  },
              }),
        awaitRefetchQueries: true,
        setVisualizationsLoading,
        setPhrasesLoading,
    });

    // Memoized so effects can safely list it in their dependency arrays
    // without re-firing on every render.
    const loadAnswerSession = useCallback(
        (
            bachSessionId: string,
            bachGenNo: number,
            acSessionId: string,
            acGenNo: number,
            bachGenNoWorkingSet?: number[],
            acGenNoWorkingSet?: number[],
        ) => {
            // Working sets are optional; omit the keys entirely when absent
            // so the variables object carries no undefined values (identical
            // on the wire, deterministic for mock-based tests).
            const variables: LoadExistingAnswerSessionMutationVariables = {
                session: {
                    sessionId: bachSessionId,
                    genNo: bachGenNo,
                    ...(bachGenNoWorkingSet
                        ? { genNoWorkingSet: bachGenNoWorkingSet }
                        : {}),
                    acSession: {
                        sessionId: acSessionId,
                        genNo: acGenNo,
                        ...(acGenNoWorkingSet
                            ? { genNoWorkingSet: acGenNoWorkingSet }
                            : {}),
                    },
                },
            };
            if (copyAnswerSession) {
                variables.copy = copyAnswerSession;
            }
            return _loadAnswerSession({
                variables: {
                    ...variables,
                },
            });
        },
        [_loadAnswerSession, copyAnswerSession],
    );

    return {
        loadAnswerSession,
        data: data?.Answer__loadSession.answer,
        loading,
        error,
    };
};
